/**
 * React Native `NotificationPlayer`.
 *
 * Core decides WHEN to play (an agent reply or component landing while the
 * widget is closed); the port only owns the how. On web that is one line —
 * `new Audio(url)`. RN has no audio playback in the platform at all: every
 * option (expo-audio, react-native-sound, react-native-video) is a native
 * module, and forcing one on integrators would mean an extra pod/gradle
 * dependency and an Expo config plugin for a notification chime.
 *
 * So the decision is inverted: the widget ships silent by default and the
 * integrator opts in with three lines against whichever player their app
 * already has. `notificationTuneUrl` from the config is handed to that player,
 * so the URL stays a config concern on every platform.
 */

import type { NotificationPlayer } from "@ringg/core";

/** What an integrator's audio library has to be able to do. */
export type PlayTune = (tuneUrl: string) => void;

/**
 * The default. Silent, and deliberately so — a widget that cannot find an
 * audio library should be quiet, not noisy about it on every reply.
 */
export const createSilentNotificationPlayer = (): NotificationPlayer => ({
  play() {
    /* no audio backend wired — see createNotificationPlayer */
  },
});

/**
 * Bridges the config's tune URL to a host-provided player.
 *
 * ```ts
 * import { createAudioPlayer } from "expo-audio";
 * const notifications = createNotificationPlayer(DEFAULT_CONFIG.notificationTuneUrl, (url) => createAudioPlayer(url).play());
 * ```
 *
 * Playback failures are swallowed: a chime is best-effort by contract, and a
 * rejected promise here must never surface mid-conversation.
 */
export const createNotificationPlayer = (tuneUrl: string, playTune: PlayTune): NotificationPlayer => ({
  play() {
    try {
      playTune(tuneUrl);
    } catch {
      /* best-effort by contract */
    }
  },
});
